import { inject, Injectable } from '@angular/core';
import { AuthService, UserProfile } from '../services/auth.service';
import { Router } from '@angular/router';
import { map, Observable } from 'rxjs';
import { normalizeRole, ERole } from '../auth/roles';
import { PERMISSIONS_BY_ROLE, Permission } from '../auth/permissions';

const SUCCESS_PREFIXES = ['/checkout-success', '/checkout-course-success', '/subscription-success'];

@Injectable({ providedIn: 'root' })
export class AccessControlService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  role$(p?: UserProfile | null): Observable<ERole | null> {
    if (p)
      return new Observable((obs) => {
        obs.next(normalizeRole(p.role));
        obs.complete();
      });
    return this.auth.userProfile$.pipe(map((pr) => normalizeRole(pr?.role)));
  }

  hasPermission$(perm: Permission): Observable<boolean> {
    return this.auth.userProfile$.pipe(
      map((p) => {
        const role = normalizeRole(p?.role);
        return !!role && PERMISSIONS_BY_ROLE[role].has(perm);
      }),
    );
  }

  hasAnyPermission$(perms: Permission[]): Observable<boolean> {
    return this.auth.userProfile$.pipe(
      map((p) => {
        const role = normalizeRole(p?.role);
        if (!role) return false;
        const granted = PERMISSIONS_BY_ROLE[role];
        return perms.some((pm) => granted.has(pm));
      }),
    );
  }

  homeFor(profile: UserProfile | null | undefined, currentUrl?: string): string | null {
    const url = (currentUrl ?? this.router.url ?? '').toString();

    if (url !== '/' && url !== '') return null;

    const role = normalizeRole(profile?.role);
    if (!role) return '/course-catalog';

    switch (role) {
      case ERole.SuperAdmin:
      case ERole.SupportAdmin:
      case ERole.GroupAdmin:
      case ERole.SubGroupAdmin:
        return '/user-management';
      case ERole.Learner:
        return '/dashboard';
      default:
        return '/course-catalog';
    }
  }
}
